import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService } from '../services/dashboardService';
import { DashboardLayout, DashboardWidget, WidgetDefinition } from '../types/Dashboard';
import DashboardGrid from '../components/dashboard/DashboardGrid';
import DashboardToolbar from '../components/dashboard/DashboardToolbar';
import WidgetLibrary from '../components/dashboard/WidgetLibrary';
import EmptyDashboard from '../components/dashboard/EmptyDashboard';
import WidgetSettings from '../components/dashboard/WidgetSettings';

const Dashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const [layout, setLayout] = useState<DashboardLayout | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showWidgetLibrary, setShowWidgetLibrary] = useState(false);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState<DashboardWidget | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSave, setLastSave] = useState<Date | null>(null);

  useEffect(() => {
    loadLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.empresaCnpj, userProfile?.id]);

  const loadLayout = async () => {
    if (!userProfile?.empresaCnpj || !userProfile?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const savedLayout = await dashboardService.getUserDashboardLayout(
        userProfile.empresaCnpj,
        userProfile.id
      );

      if (savedLayout) {
        setLayout(savedLayout);
      } else {
        // Usar layout padrão
        const defaultLayout = dashboardService.getDefaultLayout();
        setLayout(defaultLayout);
        // Salvar layout padrão
        await dashboardService.saveUserDashboardLayout(
          userProfile.empresaCnpj,
          userProfile.id,
          defaultLayout
        );
        setLayout(defaultLayout);
      }
    } catch (error) {
      console.error('Erro ao carregar layout:', error);
      // Em caso de erro, usar layout padrão
      const defaultLayout = dashboardService.getDefaultLayout();
      setLayout(defaultLayout);
      try {
        await dashboardService.saveUserDashboardLayout(
          userProfile.empresaCnpj,
          userProfile.id,
          defaultLayout
        );
      } catch (saveError) {
        console.error('Erro ao salvar layout padrão:', saveError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLayoutChange = useCallback((widgets: DashboardWidget[]) => {
    if (!layout) return;
    setLayout({
      ...layout,
      widgets
    });
  }, [layout]);

  const handleSave = async () => {
    if (!userProfile?.empresaCnpj || !userProfile?.id || !layout) return;

    try {
      setIsSaving(true);
      await dashboardService.saveUserDashboardLayout(
        userProfile.empresaCnpj,
        userProfile.id,
        layout
      );
      setLastSave(new Date());
      setIsEditing(false);
    } catch (error) {
      console.error('Erro ao salvar layout:', error);
      alert('Erro ao salvar layout. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddWidget = (widgetDef: WidgetDefinition) => {
    if (!layout) return;

    const isChartWidget = ['line-chart', 'bar-chart', 'pie-chart', 'area-chart'].includes(widgetDef.type);
    
    const newWidget: DashboardWidget = {
      id: `widget-${Date.now()}`,
      type: widgetDef.type,
      title: widgetDef.name,
      size: widgetDef.defaultSize,
      position: { x: 0, y: 0 },
      config: {
        [widgetDef.type === 'metric-card' ? 'metricType' : 
          widgetDef.type === 'line-chart' ? 'chartType' :
          widgetDef.type === 'bar-chart' ? 'chartType' :
          widgetDef.type === 'pie-chart' ? 'chartType' :
          widgetDef.type === 'area-chart' ? 'chartType' :
          widgetDef.type === 'list' ? 'listType' :
          widgetDef.type === 'stat-card' ? 'statType' :
          widgetDef.type === 'comparison' ? 'comparisonType' :
          widgetDef.type === 'evaluation-report' ? 'reportType' : 'type']: widgetDef.id,
        ...(isChartWidget && {
          dateRange: '30d' as const
        })
      }
    };

    setLayout({
      ...layout,
      widgets: [...layout.widgets, newWidget]
    });
  };

  const handleRemoveWidget = (widgetId: string) => {
    if (!layout) return;
    const remainingWidgets = layout.widgets.filter(w => w.id !== widgetId);
    
    // Recalcula posições para compactar verticalmente
    const widgetWidth = (size: string) => size === 'small' ? 4 : size === 'medium' ? 8 : 12;
    const widgetHeight = (size: string) => size === 'large' ? 6 : 3;
    
    // Ordena widgets por posição atual
    const sortedWidgets = [...remainingWidgets].sort((a, b) => {
      if (a.position.y !== b.position.y) return a.position.y - b.position.y;
      return a.position.x - b.position.x;
    });

    // Recalcula posições compactando verticalmente
    const rows: Array<{ widgets: any[], maxHeight: number, y: number }> = [];
    
    sortedWidgets.forEach((widget) => {
      const w = widgetWidth(widget.size);
      const h = widgetHeight(widget.size);
      
      // Procura uma linha onde o widget cabe
      let placed = false;
      for (const row of rows) {
        const totalWidth = row.widgets.reduce((sum, item) => sum + widgetWidth(item.size), 0);
        if (totalWidth + w <= 12) {
          // Cabe na linha
          widget.position.x = totalWidth;
          widget.position.y = row.y;
          row.widgets.push(widget);
          row.maxHeight = Math.max(row.maxHeight, h);
          placed = true;
          break;
        }
      }
      
      if (!placed) {
        // Não cabe em nenhuma linha existente, cria nova linha
        const newY = rows.length === 0 ? 0 : 
          rows.reduce((sum, row) => sum + row.maxHeight, 0);
        widget.position.x = 0;
        widget.position.y = newY;
        rows.push({ widgets: [widget], maxHeight: h, y: newY });
      }
    });

    setLayout({
      ...layout,
      widgets: sortedWidgets
    });
  };

  const handleWidgetConfigChange = (widgetId: string, config: Record<string, any>) => {
    if (!layout) return;
    setLayout({
      ...layout,
      widgets: layout.widgets.map(w => {
        if (w.id === widgetId) {
          const { title, openSettings, ...restConfig } = config;
          return {
            ...w,
            ...(title !== undefined && { title }),
            config: { ...w.config, ...restConfig }
          };
        }
        return w;
      })
    });
    setShowWidgetSettings(false);
    setSelectedWidget(null);
  };

  const handleResetLayout = () => {
    if (window.confirm('Tem certeza que deseja resetar o layout para o padrão? Esta ação não pode ser desfeita.')) {
      const defaultLayout = dashboardService.getDefaultLayout();
      setLayout(defaultLayout);
    }
  };

  const handleOpenWidgetSettings = (widget: DashboardWidget) => {
    setSelectedWidget(widget);
    setShowWidgetSettings(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (!layout || layout.widgets.length === 0) {
    return (
      <div className="space-y-6">
        <DashboardToolbar
          isEditing={isEditing}
          onToggleEdit={() => setIsEditing(!isEditing)}
          onAddWidget={() => setShowWidgetLibrary(true)}
          onResetLayout={handleResetLayout}
          onSave={handleSave}
          isSaving={isSaving}
        />
        <EmptyDashboard onAddWidget={() => setShowWidgetLibrary(true)} />
        <WidgetLibrary
          isOpen={showWidgetLibrary}
          onClose={() => setShowWidgetLibrary(false)}
          onAddWidget={handleAddWidget}
          existingWidgets={layout?.widgets || []}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DashboardToolbar
        isEditing={isEditing}
        onToggleEdit={() => setIsEditing(!isEditing)}
        onAddWidget={() => setShowWidgetLibrary(true)}
        onResetLayout={handleResetLayout}
        onSave={handleSave}
        isSaving={isSaving}
      />

      {lastSave && (
        <div className="bg-success-50 border border-success-200 text-success-700 px-4 py-2 rounded-lg text-sm">
          Layout salvo com sucesso às {lastSave.toLocaleTimeString('pt-BR')}
        </div>
      )}

      <DashboardGrid
        widgets={layout.widgets}
        onLayoutChange={handleLayoutChange}
        onRemoveWidget={handleRemoveWidget}
        onWidgetConfigChange={(widgetId, config) => {
          if (config.openSettings) {
            const widget = layout.widgets.find(w => w.id === widgetId);
            if (widget) {
              handleOpenWidgetSettings(widget);
            }
          } else {
            handleWidgetConfigChange(widgetId, config);
          }
        }}
        isEditing={isEditing}
      />

      <WidgetLibrary
        isOpen={showWidgetLibrary}
        onClose={() => setShowWidgetLibrary(false)}
        onAddWidget={handleAddWidget}
        existingWidgets={layout?.widgets || []}
      />

      <WidgetSettings
        widget={selectedWidget}
        isOpen={showWidgetSettings}
        onClose={() => {
          setShowWidgetSettings(false);
          setSelectedWidget(null);
        }}
        onSave={(config) => {
          if (selectedWidget) {
            handleWidgetConfigChange(selectedWidget.id, config);
          }
        }}
      />
    </div>
  );
};

export default Dashboard;
